import { useState, useEffect, useRef } from 'react';

interface MagnetProps {
    children: React.ReactNode;
    padding?: number;
    disabled?: boolean;
    magnetStrength?: number;
    activeTransition?: string;
    inactiveTransition?: string;
    className?: string;
}

const Magnet: React.FC<MagnetProps> = ({
    children,
    padding = 80,
    disabled = false,
    magnetStrength = 2,
    activeTransition = 'transform 0.3s ease-out',
    inactiveTransition = 'transform 0.5s ease-in-out',
    className = '',
}) => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isActive, setIsActive] = useState(false);
    const magnetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (disabled) {
            setPosition({ x: 0, y: 0 });
            return;
        }

        // Skip on touch-only devices where hover doesn't apply
        const isTouchOnly = window.matchMedia('(hover: none)').matches;
        if (isTouchOnly) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!magnetRef.current) return;
            const { left, top, width, height } = magnetRef.current.getBoundingClientRect();
            const centerX = left + width / 2;
            const centerY = top + height / 2;

            const distX = Math.abs(centerX - e.clientX);
            const distY = Math.abs(centerY - e.clientY);

            if (distX < width / 2 + padding && distY < height / 2 + padding) {
                setIsActive(true);
                setPosition({
                    x: (e.clientX - centerX) / magnetStrength,
                    y: (e.clientY - centerY) / magnetStrength,
                });
            } else {
                setIsActive(false);
                setPosition({ x: 0, y: 0 });
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [padding, disabled, magnetStrength]);

    return (
        <div
            ref={magnetRef}
            className={className}
            style={{ position: 'relative', display: 'inline-block' }}
        >
            <div
                style={{
                    transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
                    transition: isActive ? activeTransition : inactiveTransition,
                    willChange: 'transform',
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default Magnet;
